import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Inbox, Lock } from "lucide-react";
import { api } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import ScreenHeader from "../components/layout/ScreenHeader";
import TransactionCard from "../components/cards/TransactionCard";
import EmptyState from "../components/EmptyState";
import EditTransactionDialog from "../components/EditTransactionDialog";
import TransactionDetailSheet from "../components/TransactionDetailSheet";
import ScopeToggle from "../components/ScopeToggle";

const TYPE_FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "ingreso", label: "Ingresos" },
  { key: "gasto", label: "Gastos" },
  { key: "transferencia", label: "Transfers" },
];

const PERIOD_FILTERS = [
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "all", label: "Todo" },
];

function isInClosedMonth(isoDate, closedSet) {
  try {
    const d = new Date(isoDate);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
    return closedSet.has(key);
  } catch {
    return false;
  }
}

export default function Movimientos() {
  const { scope } = useScope();
  const [txs, setTxs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [closedSet, setClosedSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("todos");
  const [periodFilter, setPeriodFilter] = useState("30d");
  const [accountFilter, setAccountFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, aRes, cRes] = await Promise.all([
        api.get("/transactions", { params: { scope } }),
        api.get("/accounts", { params: { scope } }),
        api.get("/months/closed", { params: { scope } }),
      ]);
      setTxs(tRes.data);
      setAccounts(aRes.data);
      setClosedSet(new Set(cRes.data.map((m) => `${m.year}-${m.month}`)));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
    const onCreate = () => load();
    window.addEventListener("fp:tx-created", onCreate);
    return () => window.removeEventListener("fp:tx-created", onCreate);
  }, [load]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const periodMs =
      periodFilter === "7d" ? 7 * 86400000 :
      periodFilter === "30d" ? 30 * 86400000 :
      Infinity;
    const q = search.trim().toLowerCase();
    return txs.filter((t) => {
      if (typeFilter !== "todos" && t.type !== typeFilter) return false;
      if (accountFilter !== "all" && t.account_id !== accountFilter) return false;
      try {
        if (periodMs !== Infinity) {
          const td = new Date(t.date).getTime();
          if (now - td > periodMs) return false;
        }
      } catch (_) {
        /* invalid date */
      }
      if (q && !(`${t.category} ${t.note || ""} ${t.account_name || ""}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [txs, typeFilter, periodFilter, accountFilter, search]);

  return (
    <div data-testid="movimientos-screen">
      <ScreenHeader
        eyebrow="Actividad"
        title="Movimientos"
        subtitle={scope === "shared"
          ? "Todo el movimiento del dinero común. Toca para editar; los meses cerrados están bloqueados."
          : "Toca cualquiera para editarlo o eliminarlo. Los meses cerrados están bloqueados."}
      />

      <div className="px-6 mb-3">
        <ScopeToggle />
      </div>

      {/* Search */}
      <div className="px-6">
        <div className="flex items-center gap-2 bg-white border border-line rounded-2xl px-4 py-3">
          <Search size={16} className="text-soft" />
          <input
            data-testid="movimientos-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por categoría o nota"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 mt-4 flex gap-2 overflow-x-auto pb-2 -mx-1">
        {TYPE_FILTERS.map((f) => (
          <FilterChip
            key={f.key}
            active={typeFilter === f.key}
            onClick={() => setTypeFilter(f.key)}
            label={f.label}
            testid={`filter-type-${f.key}`}
          />
        ))}
        <span className="w-px bg-line mx-1" />
        {PERIOD_FILTERS.map((f) => (
          <FilterChip
            key={f.key}
            active={periodFilter === f.key}
            onClick={() => setPeriodFilter(f.key)}
            label={f.label}
            testid={`filter-period-${f.key}`}
          />
        ))}
      </div>

      {/* Account filter */}
      <div className="px-6 mt-2">
        <select
          data-testid="filter-account"
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          className="w-full bg-white border border-line rounded-xl px-3 py-2.5 text-sm outline-none focus:border-sage-500"
        >
          <option value="all">Todas las cuentas</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className="px-6 mt-4">
        <div className="bg-white border border-line/70 rounded-3xl shadow-card px-5">
          {loading ? (
            <div className="py-10 text-center text-soft text-sm">Cargando…</div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title={scope === "shared" ? "Sin movimientos compartidos" : "Sin movimientos"}
              description={
                scope === "shared"
                  ? "Cuando registren ingresos o gastos en una cuenta compartida aparecerán aquí. Son del dinero común."
                  : "Cuando registres ingresos o gastos aparecerán aquí."
              }
              testid="movimientos-empty"
            />
          ) : (
            filtered.map((t) => {
              const locked = isInClosedMonth(t.date, closedSet);
              return (
                <div
                  key={t.id}
                  data-testid={`tx-row-${t.id}`}
                  className="relative"
                >
                  <TransactionCard
                    tx={t}
                    testid={`tx-${t.id}`}
                    onClick={() => setDetail(t)}
                  />
                  {locked && (
                    <Lock size={14} className="absolute right-1 top-1/2 -translate-y-1/2 text-soft" aria-label="Mes cerrado" />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <TransactionDetailSheet
        tx={detail}
        onClose={() => setDetail(null)}
        locked={detail ? isInClosedMonth(detail.date, closedSet) : false}
        onEdit={() => {
          if (detail && !isInClosedMonth(detail.date, closedSet)) {
            setEditing(detail);
            setDetail(null);
          }
        }}
      />

      <EditTransactionDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        tx={editing}
        accounts={accounts}
        onChanged={load}
      />
    </div>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
        active
          ? "bg-sage-900 text-bone border-sage-900"
          : "bg-white text-ink border-line hover:border-sage-200"
      }`}
    >
      {label}
    </button>
  );
}
